import { Area, AreaChart, DotProps, ResponsiveContainer, Tooltip, TooltipProps } from 'recharts';
import { MetricDetailsItemResponseDto, ValueType } from '../../types/response';

type KpiMetricGraphDataPoint = {
  key: string;
  label: string;
  value: number;
  valueType: ValueType;
};

type RechartsDotProps = DotProps & {
  key?: string;
  index?: number;
};

interface KpiMetricGraphProps {
  items: MetricDetailsItemResponseDto[];
  valueType?: ValueType;
  className?: string;
}

const formatValue = (value: number, valueType: ValueType) => {
  if (valueType === 'currency') return value.toLocaleString('de-DE') + '€';
  if (valueType === 'percentage') return value.toLocaleString('de-DE') + '%';
  return value.toLocaleString('de-DE');
};

const CustomTooltip = ({ active, payload }: TooltipProps<number, string>) => {
  if (!active || !payload || payload.length === 0) return null;

  const point = payload[0]?.payload as (KpiMetricGraphDataPoint & { valueType: ValueType }) | undefined;
  if (!point) return null;

  return (
    <div className="rounded-md bg-[#12282A] px-2 py-1 text-xs text-white shadow-sm">
      <span className="font-semibold">{point.label}: </span>
      <span>{formatValue(point.value, point.valueType)}</span>
    </div>
  );
};

const HighlightDot = ({ cx, cy, dotKey }: { cx?: number; cy?: number; dotKey?: string }) => {
  if (cx == null || cy == null) return null;

  return (
    <g key={dotKey}>
      <circle cx={cx} cy={cy} r={10} fill="none" stroke="#12282A" strokeWidth={2} opacity={0.2} />
      <circle cx={cx} cy={cy} r={4.5} fill="url(#kpiMetricDotGradient)" />
      <circle cx={cx} cy={cy} r={4.5} fill="none" stroke="#12282A" strokeWidth={1.5} />
    </g>
  );
};

const KpiMetricGraph = ({ items, valueType = 'integer', className }: KpiMetricGraphProps) => {
  const data: KpiMetricGraphDataPoint[] = items.map((item) => ({
    key: `${item.year}-${item.week}`,
    label: `${item.year} | KW ${item.week}`,
    value: Number.isFinite(item.value) ? item.value : 0,
    valueType
  }));

  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height={100}>
        <AreaChart data={data} margin={{ top: 6, right: 2, left: 2, bottom: 0 }}>
          <defs>
            <linearGradient id="kpiMetricFillGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#C0DE60" stopOpacity={0.28} />
              <stop offset="100%" stopColor="#5FD5DE" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="kpiMetricStrokeGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#5FD5DE" />
              <stop offset="100%" stopColor="#B9D95F" />
            </linearGradient>
            <linearGradient id="kpiMetricDotGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#C0DE60" />
              <stop offset="100%" stopColor="#5FD5DE" />
            </linearGradient>
          </defs>

          <Area
            type="monotone"
            dataKey="value"
            stroke="url(#kpiMetricStrokeGradient)"
            strokeWidth={2.5}
            fillOpacity={1}
            fill="url(#kpiMetricFillGradient)"
            dot={(props: RechartsDotProps) => {
              const { key, index, cx, cy } = props;
              if (index !== data.length - 1) return <g key={key} />;
              return <HighlightDot key={key} cx={cx} cy={cy} dotKey={key} />;
            }}
            activeDot={(props: RechartsDotProps) => {
              const { key, cx, cy } = props;
              return <HighlightDot key={key} cx={cx} cy={cy} dotKey={key} />;
            }}
          />
          <Tooltip content={<CustomTooltip />} cursor={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default KpiMetricGraph;
